import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { initialize, type LDClient, type LDContext, type LDFlagSet } from "launchdarkly-js-client-sdk";
import { type ZodType } from "zod";
import { packsizeZod } from "@packsize/utils";

import type { LaunchDarklyOptions } from "../schemas/launch-darkly-options";

import { FeatureFlagsContext, type FeatureFlagIdentity } from "./feature-flags.context";

const GLOBAL_KEY = "__packsize_launch_darkly_client__";
const IDENTIFY_RETRY_BASE_DELAY_MS = 1000;
const IDENTIFY_RETRY_MAX_ATTEMPTS = 3;

const getGlobalClient = (): LDClient | null => window[GLOBAL_KEY] ?? null;

const setGlobalClient = (client: LDClient): void => {
	window[GLOBAL_KEY] = client;
};

const defaultContext = {
	kind: "user",
	anonymous: true,
};

const getLDContext = (featureFlagIdentity: FeatureFlagIdentity | undefined): LDContext => {
	if (featureFlagIdentity === undefined) {
		return defaultContext;
	}

	return {
		kind: "multi",

		user: featureFlagIdentity.userId
			? {
					kind: "user",
					key: `user-${featureFlagIdentity.userId}`,
					anonymous: false,
					email: featureFlagIdentity?.email,
					userName: featureFlagIdentity?.userName,
				}
			: undefined,

		email: featureFlagIdentity.email
			? {
					kind: "email",
					key: featureFlagIdentity.email,
					anonymous: false,
				}
			: undefined,

		tenant: featureFlagIdentity.tenantId
			? {
					kind: "tenant",
					key: `tenant-${featureFlagIdentity.tenantId}`,
					anonymous: false,
				}
			: undefined,

		salesforce: featureFlagIdentity.salesforceId
			? {
					kind: "salesforce",
					key: `salesforce-${featureFlagIdentity.salesforceId}`,
					anonymous: false,
				}
			: undefined,

		company: featureFlagIdentity.companyId
			? {
					kind: "company",
					key: `company-${featureFlagIdentity.companyId}`,
					anonymous: false,
				}
			: undefined,
	};
};

interface FeatureFlagsProviderProps {
	children: React.ReactNode;
	launchDarklyOptions: LaunchDarklyOptions;
	loadingComponent: React.ReactNode;
}

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
	children,
	launchDarklyOptions,
	loadingComponent,
}) => {
	const [loadingFlags, setLoadingFlags] = useState(false);
	const [identityHealthy, setIdentityHealthy] = useState(true);
	const [featureFlagsInitialized, setFeatureFlagsInitialized] = useState(false);
	const identityRequestIdRef = useRef(0);
	const lastFeatureFlagIdentityRef = useRef<FeatureFlagIdentity | undefined>(undefined);
	const identityRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const identityRetryAttemptRef = useRef(0);
	const applyFeatureFlagIdentityRef = useRef<
		(featureFlagIdentity: FeatureFlagIdentity | undefined, isRetry: boolean) => Promise<void>
	>(async () => undefined);

	const clearIdentityRetry = useCallback(() => {
		if (identityRetryTimeoutRef.current === undefined) {
			return;
		}

		clearTimeout(identityRetryTimeoutRef.current);
		identityRetryTimeoutRef.current = undefined;
	}, []);

	useEffect(() => {
		return () => {
			if (identityRetryTimeoutRef.current === undefined) {
				return;
			}

			clearTimeout(identityRetryTimeoutRef.current);
		};
	}, []);

	const applyFeatureFlagIdentity = useCallback(
		async (featureFlagIdentity: FeatureFlagIdentity | undefined, isRetry: boolean): Promise<void> => {
			lastFeatureFlagIdentityRef.current = featureFlagIdentity;

			if (isRetry === false) {
				identityRetryAttemptRef.current = 0;
				clearIdentityRetry();
			}

			identityRequestIdRef.current += 1;
			const requestId = identityRequestIdRef.current;
			const isCurrentRequest = (): boolean => requestId === identityRequestIdRef.current;

			if (isRetry === false) {
				setLoadingFlags(true);
			}

			try {
				const client = getGlobalClient();

				if (client === null) {
					if (isCurrentRequest() === true) {
						setIdentityHealthy(true);
					}
					return;
				}

				const newLDContext = getLDContext(featureFlagIdentity);

				await client.identify(newLDContext);
				if (isCurrentRequest() === true) {
					identityRetryAttemptRef.current = 0;
					clearIdentityRetry();
					setIdentityHealthy(true);
				}
			} catch (error: unknown) {
				console.error("PackNet user experience degraded due to failure to identify LaunchDarkly context.", error);
				if (isCurrentRequest() === false) {
					return;
				}

				setIdentityHealthy(false);
				if (identityRetryAttemptRef.current >= IDENTIFY_RETRY_MAX_ATTEMPTS) {
					return;
				}

				const delayMs = IDENTIFY_RETRY_BASE_DELAY_MS * 2 ** identityRetryAttemptRef.current;
				identityRetryAttemptRef.current += 1;
				identityRetryTimeoutRef.current = setTimeout(() => {
					void applyFeatureFlagIdentityRef.current(lastFeatureFlagIdentityRef.current, true);
				}, delayMs);
			} finally {
				if (isCurrentRequest() === true && isRetry === false) {
					setLoadingFlags(false);
				}
			}
		},
		[clearIdentityRetry],
	);

	useEffect(() => {
		applyFeatureFlagIdentityRef.current = applyFeatureFlagIdentity;
	}, [applyFeatureFlagIdentity]);

	const setFeatureFlagIdentity = useCallback(
		(featureFlagIdentity: FeatureFlagIdentity | undefined): Promise<void> => {
			return applyFeatureFlagIdentity(featureFlagIdentity, false);
		},
		[applyFeatureFlagIdentity],
	);

	const isFeatureFlagEnabled = useMemo(() => {
		function isFeatureFlagEnabled(featureFlag: string): boolean;
		function isFeatureFlagEnabled<T>(featureFlag: string, defaultValue: T, schema: ZodType<T>): T;
		function isFeatureFlagEnabled<T>(featureFlag: string, defaultValue?: T, schema?: ZodType<T>): T | boolean {
			const resolvedSchema = schema ?? packsizeZod.coerceBoolean;
			const fallback = defaultValue !== undefined ? defaultValue : false;

			if (loadingFlags === true) {
				return fallback;
			}

			const client = getGlobalClient();

			if (client !== null && identityHealthy === true) {
				const rawValue = client.variation(featureFlag, fallback);
				const result = resolvedSchema.safeParse(rawValue);

				if (result.success === true) {
					return result.data;
				}

				return fallback;
			}

			return fallback;
		}

		return isFeatureFlagEnabled;
	}, [identityHealthy, loadingFlags]);

	const getAllFeatureFlags = useCallback((): LDFlagSet => {
		if (loadingFlags === true) {
			return {};
		}

		const client = getGlobalClient();

		if (client !== null && identityHealthy === true) {
			return client.allFlags();
		}

		return {};
	}, [identityHealthy, loadingFlags]);

	const doLoad = useCallback(async () => {
		const existing = getGlobalClient();

		if (existing !== null) {
			setFeatureFlagsInitialized(true);
			return;
		}

		const newClient = initialize(launchDarklyOptions.clientSideId, defaultContext, {
			sendEventsOnlyForVariation: true,
			baseUrl: launchDarklyOptions.baseUrl,
			streamUrl: launchDarklyOptions.streamUrl,
			eventsUrl: launchDarklyOptions.eventsUrl,
		});

		try {
			await newClient.waitForInitialization(5);
			setGlobalClient(newClient);
			setFeatureFlagsInitialized(true);
		} catch {
			console.error(
				"PackNet user experience degraded due to failure to connect to LaunchDarkly. Ensure LaunchDarkly is on allow list.",
			);

			setFeatureFlagsInitialized(true);
		}
	}, [
		launchDarklyOptions.clientSideId,
		launchDarklyOptions.baseUrl,
		launchDarklyOptions.streamUrl,
		launchDarklyOptions.eventsUrl,
	]);

	useEffect(() => {
		doLoad();
	}, [doLoad]);

	const value = useMemo(
		() => ({
			getAllFeatureFlags,
			isFeatureFlagEnabled,
			setFeatureFlagIdentity,
		}),
		[getAllFeatureFlags, isFeatureFlagEnabled, setFeatureFlagIdentity],
	);

	return (
		<FeatureFlagsContext.Provider value={value}>
			{featureFlagsInitialized === false && loadingComponent}
			{featureFlagsInitialized === true && children}
		</FeatureFlagsContext.Provider>
	);
};
